import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowRightAlt, ArrowDownward, PhotoCamera, Campaign, CheckCircle } from '@mui/icons-material';

const HowItWorks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      title: 'Capture',
      description:
        'Snap a photo or short video of an environmental issue in your surroundings.',
      icon: <PhotoCamera sx={{ fontSize: 42, color: '#4caf50' }} />,
      color: 'rgba(76, 175, 80, 0.2)', // eco green
    },
    {
      title: 'Report',
      description:
        'Describe the issue, pick a category, and let MazGuard tag your location.',
      icon: <Campaign sx={{ fontSize: 42, color: '#0288d1' }} />,
      color: 'rgba(2, 136, 209, 0.2)', // water blue
    },
    {
      title: 'Solve',
      description:
        'Your report is tracked and acted upon by communities & authorities.',
      icon: <CheckCircle sx={{ fontSize: 42, color: '#f9a825' }} />,
      color: 'rgba(249, 168, 37, 0.25)', // earthy yellow
    },
  ];

  return (
    <Box
      sx={{
        py: 10,
        px: 3,
        // backgroundImage: `url('https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1600&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
      }}
    >
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 60, 30, 0.5)',
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          maxWidth: 1200,
          mx: 'auto',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight={700}
            sx={{ color: '#fff', mb: 8, fontFamily: 'Playfair Display, serif' }}
          >
             How MazGuard Works
          </Typography>
        </motion.div>

        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={isMobile ? 6 : 4}
          alignItems="center"
          justifyContent="center"
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Card
                  sx={{
                    borderRadius: '20px',
                    p: 3,
                    minHeight: 250,
                    width: isMobile ? '100%' : 300,
                    backgroundColor: step.color,
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0px 6px 24px rgba(0,0,0,0.2)',
                    color: '#fff',
                    textAlign: 'center',
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 12px 32px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ mb: 2 }}>{step.icon}</Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.9)' }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Arrows between cards */}
              {index < steps.length - 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.3 }}
                  viewport={{ once: true }}
                >
                  {isMobile ? (
                    <ArrowDownward
                      sx={{ fontSize: 36, color: '#fff', my: 2 }}
                    />
                  ) : (
                    <ArrowRightAlt
                      sx={{ fontSize: 42, color: '#fff', mx: 2 }}
                    />
                  )}
                </motion.div>
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default HowItWorks;
